import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { setPageMetadata } from '../services/pageMetadata';

// Update these two when things change.
const UPDATED = 'July 2026';
const CONTACT_EMAIL = 'hello@what-to-binge.com';

// --- small styled primitives so we don't need a typography plugin ---
const H2 = ({ children }) => (
  <h2 className="font-serif text-binge-ink mt-9 mb-2" style={{ fontSize: 22 }}>{children}</h2>
);
const P = ({ children }) => (
  <p className="text-binge-soft mb-3" style={{ fontSize: 15, lineHeight: 1.7 }}>{children}</p>
);
const UL = ({ children }) => (
  <ul className="mb-3 pl-5" style={{ listStyle: 'disc' }}>{children}</ul>
);
const LI = ({ children }) => (
  <li className="text-binge-soft mb-1" style={{ fontSize: 15, lineHeight: 1.7 }}>{children}</li>
);
const Mail = () => (
  <a href={`mailto:${CONTACT_EMAIL}`} className="text-binge-gold hover:underline">{CONTACT_EMAIL}</a>
);

// --- page content keyed by slug ---
const PAGES = {
  about: {
    title: 'About',
    body: (
      <>
        <P>
          What to Binge is a semantic discovery engine for movies, series and
          manhwa. Instead of forcing you to pick a genre, it understands
          plain-English intent — “a slow-burn revenge thriller with a quiet
          lead” — and returns explainable, region-aware recommendations.
        </P>
        <P>
          Under the hood it pairs vector search (for meaning) with a knowledge
          graph (for stored relationships such as shared cast, directors, genres
          and catalog descriptors). Public title intelligence labels observations,
          deterministic derivations and inferred interpretations separately.
        </P>
        <P>
          It’s an independent project. We don’t host or stream any content — we
          point you to where titles are legally available in your region.
          Questions or feedback? Reach us at <Mail />.
        </P>
      </>
    ),
  },

  contact: {
    title: 'Contact',
    body: (
      <>
        <P>We’d love to hear from you — feedback, bug reports, partnerships, or
          data/privacy requests.</P>
        <H2>Email</H2>
        <P><Mail /></P>
        <H2>Data & privacy requests</H2>
        <P>
          To access or delete your account data, email us from the address you
          signed up with and we’ll action it. See our{' '}
          <Link to="/privacy" className="text-binge-gold hover:underline">Privacy Policy</Link>{' '}
          for details.
        </P>
      </>
    ),
  },

  terms: {
    title: 'Terms of Service',
    body: (
      <>
        <P>Last updated: {UPDATED}</P>
        <P>
          By accessing or using What to Binge (the “Service”), you agree to these
          Terms. If you don’t agree, please don’t use the Service.
        </P>

        <H2>1. What the Service is</H2>
        <P>
          What to Binge is a search and recommendation tool that helps you
          discover titles and find where they are available to watch or read. We
          are <strong>not</strong> a streaming service and we do <strong>not</strong>{' '}
          host, store, or distribute any films, series, or comic content.
        </P>

        <H2>2. Third-party content & attribution</H2>
        <P>
          Catalog metadata, artwork and availability come from third parties
          including The Movie Database (TMDB), MangaDex and AniList. This product
          uses these APIs but is not endorsed or certified by them. All titles,
          posters, and trademarks are the property of their respective owners.
        </P>

        <H2>3. Accounts</H2>
        <P>
          Some features require an account. You’re responsible for keeping your
          login secure and for activity under your account. You must provide
          accurate information and be old enough to form a binding contract in
          your jurisdiction.
        </P>

        <H2>4. Acceptable use</H2>
        <P>You agree not to:</P>
        <UL>
          <LI>scrape, overload, or attempt to disrupt the Service;</LI>
          <LI>reverse-engineer or resell the Service without permission;</LI>
          <LI>use it for any unlawful purpose or to infringe others’ rights.</LI>
        </UL>

        <H2>5. “As is”, no warranty</H2>
        <P>
          The Service is provided “as is” and “as available”, without warranties
          of any kind. Recommendations and availability data may be inaccurate or
          out of date; always confirm on the provider’s own site.
        </P>

        <H2>6. Limitation of liability</H2>
        <P>
          To the maximum extent permitted by law, What to Binge and its operator
          are not liable for any indirect, incidental, or consequential damages
          arising from your use of the Service.
        </P>

        <H2>7. Changes</H2>
        <P>
          We may update these Terms from time to time. Continued use after changes
          means you accept the updated Terms. Material changes will update the date
          above.
        </P>

        <H2>8. Contact</H2>
        <P>Questions about these Terms? Email <Mail />.</P>
      </>
    ),
  },

  privacy: {
    title: 'Privacy Policy',
    body: (
      <>
        <P>Last updated: {UPDATED}</P>
        <P>
          This policy explains what we collect, why, and your choices. We keep it
          minimal: we don’t sell your personal data.
        </P>

        <H2>1. What we collect</H2>
        <UL>
          <LI>
            <strong>Account data</strong> — if you sign up, your email address and
            authentication details, handled by our auth provider (Supabase). If
            you use “Continue with Google”, Google shares your basic profile (name,
            email) per your consent.
          </LI>
          <LI>
            <strong>Usage analytics</strong> — we use Mixpanel to understand how
            the product is used. This includes events such as searches you run
            (including the search text), pages viewed, and basic device/browser
            information. This helps us improve recommendations and fix what’s
            broken.
          </LI>
          <LI>
            <strong>Local storage</strong> — we store preferences and your consent
            choice in your browser’s local storage so the site works and remembers
            your settings.
          </LI>
          <LI>
            <strong>Business beta inquiries</strong> — if you request BingeIQ beta
            access, we collect the name, work email, company, role, territories,
            use case and optional message you submit. This is stored separately
            from consumer analytics and used only to review and respond to the inquiry.
          </LI>
        </UL>

        <H2>2. Cookies & advertising</H2>
        <P>
          We may show ads from third-party networks (e.g. Google AdSense). With
          your consent, these partners may use cookies or similar technologies to
          serve and measure ads. You control this via the consent banner, and ads
          are never shown on reading pages.
        </P>

        <H2>3. Third parties we share with</H2>
        <P>
          We only share what’s needed to run the Service: Supabase (authentication),
          Mixpanel (analytics), and Google (sign-in and, where enabled, ads). Title
          metadata is fetched from TMDB, MangaDex and AniList. Each has its own
          privacy policy.
        </P>

        <H2>4. Your choices & rights</H2>
        <UL>
          <LI>Decline non-essential cookies via the consent banner.</LI>
          <LI>Access or delete your account data — email us from your sign-up address.</LI>
          <LI>Access or delete a business inquiry — email us from the work address submitted.</LI>
          <LI>Use most of the site without an account at all.</LI>
        </UL>

        <H2>5. Data retention</H2>
        <P>
          We keep account data while your account exists, business inquiries while
          reasonably needed to evaluate and operate the beta, and analytics data only
          as long as useful for product improvement. Delete requests are honored promptly.
        </P>

        <H2>6. Children</H2>
        <P>
          The Service isn’t directed at children under 13 (or the minimum age in
          your country), and we don’t knowingly collect their data.
        </P>

        <H2>7. Contact</H2>
        <P>Privacy questions or requests? Email <Mail />.</P>
      </>
    ),
  },
};

const LegalPage = ({ slug }) => {
  const page = PAGES[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
    setPageMetadata({
      title: `${(page || PAGES.about).title} | What to Binge`,
      description: `${(page || PAGES.about).title} information for What to Binge, the semantic film, series and public content-intelligence experience.`,
      canonical: `https://what-to-binge.com/${page ? slug : 'about'}`,
    });
  }, [slug, page]);

  // Defensive: unknown slug shouldn't render blank.
  const content = page || PAGES.about;

  return (
    <div className="min-h-screen bg-binge-bg text-binge-ink flex flex-col">
      {/* Top bar */}
      <header style={{ borderBottom: '1px solid rgba(241,236,226,0.08)' }}>
        <div className="mx-auto px-6 sm:px-10 py-5" style={{ maxWidth: 1440 }}>
          <Link to="/" className="text-binge-sub hover:text-binge-ink transition-colors" style={{ fontSize: 14 }}>
            ← What to Binge
          </Link>
        </div>
      </header>

      {/* Body */}
      <main className="flex-1 mx-auto px-6 sm:px-10 py-12 w-full" style={{ maxWidth: 760 }}>
        <h1 className="font-serif text-binge-ink mb-6" style={{ fontSize: 34, lineHeight: 1.15 }}>
          {content.title}
        </h1>
        {content.body}
      </main>

      <Footer />
    </div>
  );
};

export default LegalPage;
